import { Link, usePage } from '@inertiajs/react'
import { type ReactNode, useMemo, useState } from 'react'
import { Bell, Menu, X } from 'lucide-react'

import { buildNavigation } from '@/config/navigation'
import type { AuthContext, AuthRole, SharedPageProps } from '@/types/shared'
import { FlashStack } from '@/components/feedback/FlashStack'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'

type AppShellProps = {
  children: ReactNode
}

const logoutPaths: Partial<Record<AuthRole, string>> = {
  player: '/player/logout',
  team: '/team/logout',
  school: '/school/logout',
}

export function AppShell({ children }: AppShellProps) {
  const { props, url } = usePage<SharedPageProps>()
  const { auth, flash, appVersion } = props

  const [mobileOpen, setMobileOpen] = useState(false)

  const navigation = useMemo(() => buildNavigation(auth?.role ?? null, url), [auth?.role, url])

  if (!auth) {
    return (
      <div className="relative min-h-screen bg-gradient-to-b from-surface-900 via-surface-900 to-surface-800">
        <FlashStack flash={flash ?? {}} />
        <div className="absolute inset-0 grid-overlay opacity-30" aria-hidden />
        <div className="relative mx-auto flex min-h-screen w-full max-w-4xl items-center justify-center px-6 py-16">
          <div className="w-full max-w-3xl rounded-3xl border border-white/10 bg-white/5 p-10 shadow-elevated">
            {children}
          </div>
        </div>
      </div>
    )
  }

  const logoutHref = auth.role ? logoutPaths[auth.role] : undefined

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-surface-900 via-surface-900 to-surface-800 text-white">
      <FlashStack flash={flash ?? {}} />
      <div className="pointer-events-none absolute inset-0 grid-overlay opacity-20" aria-hidden />
      <div className="relative flex min-h-screen">
        {/* Desktop navigation */}
        <aside className="hidden w-72 border-r border-white/5 bg-surface-900/80 px-6 py-10 lg:flex lg:flex-col lg:justify-between">
          <div className="space-y-10">
            <div>
              <Link href="/" className="flex items-center gap-3 text-lg font-semibold text-white">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-500 text-xl font-bold shadow-soft">
                  TB
                </div>
                <span>Talentbox</span>
              </Link>
            </div>
            <nav className="space-y-8">
              {navigation.map((section, idx) => (
                <div key={idx} className="space-y-3">
                  {section.title ? <p className="text-xs uppercase tracking-wider text-white/40">{section.title}</p> : null}
                  <div className="space-y-2">
                    {section.items.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        className={cn(
                          'group flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-white/70 transition hover:bg-white/10 hover:text-white',
                          item.active && 'bg-brand-500/15 text-white shadow-soft',
                        )}
                      >
                        <item.icon className="h-4 w-4 text-brand-300" />
                        <div className="flex-1">
                          <div>{item.label}</div>
                          {item.description ? <p className="text-xs text-white/50">{item.description}</p> : null}
                        </div>
                        {item.badgeCount && item.badgeCount > 0 ? (
                          <span className="rounded-full bg-brand-500/20 px-2 py-1 text-xs text-brand-100">
                            {item.badgeCount}
                          </span>
                        ) : null}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </div>
          <div className="space-y-3 text-xs text-white/50">
            <p>バージョン {appVersion}</p>
            <p>&copy; {new Date().getFullYear()} Talentbox</p>
          </div>
        </aside>

        {/* Mobile navigation */}
        {mobileOpen ? (
          <div
            className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
        ) : null}
        <div
          id="mobile-navigation"
          className={cn(
            'fixed inset-y-0 left-0 z-40 w-72 transform border-r border-white/5 bg-surface-900/95 px-6 py-10 transition duration-300 lg:hidden',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex items-center justify-between">
            <Link href="/" className="flex items-center gap-3 text-lg font-semibold text-white">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-500 text-xl font-bold shadow-soft">
                TB
              </div>
              <span>Talentbox</span>
            </Link>
            <Button variant="ghost" size="sm" onClick={() => setMobileOpen(false)} aria-label="メニューを閉じる">
              <X className="h-5 w-5" />
            </Button>
          </div>
          <nav className="mt-10 space-y-6">
            {navigation.map((section, idx) => (
              <div key={idx} className="space-y-2">
                {section.title ? <p className="text-xs uppercase tracking-wider text-white/40">{section.title}</p> : null}
                {section.items.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={cn(
                      'block rounded-2xl px-4 py-3 text-sm font-medium text-white/70 transition hover:bg-white/10 hover:text-white',
                      item.active && 'bg-brand-500/20 text-white',
                    )}
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            ))}
          </nav>
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 border-b border-white/5 bg-surface-900/70 backdrop-blur">
            <div className="flex items-center justify-between gap-6 px-6 py-4">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="lg:hidden"
                  onClick={() => setMobileOpen((prev) => !prev)}
                  aria-expanded={mobileOpen}
                  aria-controls="mobile-navigation"
                  aria-label="メニューを開閉"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" className="relative">
                  <Bell className="h-5 w-5" />
                  <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-brand-400" />
                </Button>
                <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-500/50 text-sm font-semibold">
                    {auth.user.initials}
                  </div>
                  <div className="hidden flex-col text-left text-sm sm:flex">
                    <span className="font-medium text-white">{auth.user.name}</span>
                    <span className="text-xs text-white/50">{roleLabel(auth)}</span>
                  </div>
                  {logoutHref ? (
                    <Link
                      href={logoutHref}
                      method="delete"
                      as="button"
                      className="rounded-full border border-white/10 px-3 py-1 text-xs font-medium text-white/70 transition hover:bg-white/10 hover:text-white"
                    >
                      ログアウト
                    </Link>
                  ) : null}
                </div>
              </div>
            </div>
          </header>
          <main className="min-w-0 flex-1 px-6 pb-16 pt-10">
            <div className="mx-auto max-w-6xl space-y-10">{children}</div>
          </main>
        </div>
      </div>
    </div>
  )
}

function roleLabel(auth: Exclude<AuthContext, null>) {
  switch (auth.role) {
    case 'player':
      return auth.user.teamName ? `${auth.user.teamName} / 選手` : '選手'
    case 'team':
      return auth.user.teamName ? `${auth.user.teamName} / チーム代表` : 'チーム代表'
    case 'school':
      return auth.user.schoolName ? `${auth.user.schoolName} / コーチ` : '学校コーチ'
    case 'admin':
      return '管理者'
    default:
      return ''
  }
}
